/**
 * 論命提示詞組裝（複製到剪貼簿用）
 *
 * 本檔只產生給「外部 AI」嘅提示詞；唔聲稱本網頁內嵌《八字提要》全文。
 * 未來若有「論命」API，可重用 buildLunmingPrompt()；而家唔需要 API key。
 */

import type { BaziResult, DayunItem, PillarDetail, Wuxing } from './bazi';
import type { ShichenCompare } from './shichen';
import { formatSampleLabel } from './shichen';

export interface LunmingPromptInput {
  /** 原局輸入公曆 */
  gregorianLabel: string;
  gender: string;
  cmp: ShichenCompare;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function formatPillarsLine(pillars: PillarDetail[]): string {
  return pillars.map((p) => `${p.label}${p.gz}`).join('　');
}

function formatShiShenSummary(r: BaziResult): string {
  const lines = r.pillars.map((p) => {
    const cang = p.canggan.map((c) => `${c.gan}${c.shiShen}`).join('、');
    const ganPart = p.label === '日柱' ? `日主${p.gan}` : `${p.gan}${p.ganShiShen}`;
    return `${p.label} ${p.gz}｜天干：${ganPart}｜藏干：${cang || '—'}`;
  });
  return lines.join('\n');
}

function formatDayunList(items: DayunItem[]): string {
  if (!items.length) return '（無）';
  return items
    .map((it) => `第${it.index}運 ${it.gz}（約${it.startAge}–${it.endAge}歲／${it.startYear}–${it.endYear}；天干十神${it.ganShiShen}）`)
    .join('\n');
}

function formatWuxing(r: BaziResult): string {
  const wx = ['木', '火', '土', '金', '水'] as Wuxing[];
  const vis = wx.map((k) => `${k}${r.wuxing.visible[k]}`).join('　');
  const cg = wx.map((k) => `${k}${r.wuxing.withCanggan[k]}`).join('　');
  return `可見干支：${vis}\n含藏干合計：${cg}`;
}

function hourGz(r: BaziResult): string {
  return r.pillars.find((p) => p.label === '時柱')?.gz ?? '';
}

/** 系統指示（外掛 AI 須跟；本站唔內嵌原書） */
export function buildSystemInstructions(prevHourGz: string, nextHourGz: string): string {
  return `【系統指示｜子平論命助手】
你係嚴謹嘅子平（Ziping）八字論命助手。請嚴格按照韋千里《八字提要》嘅格局、用神、喜忌同論斷框架嚟分析；優先依書中分類同術語，唔好用農場文章、網文雜湊、或紫微斗數體系。

硬性約束：
1. 只論子平八字；禁止引入紫微斗數、玄空風水、盲目神煞堆砌、或來源不明嘅「農場／網文命理」。
2. 本對話提供嘅四柱／十神／大運／五行係排盤數據；你唔可以聲稱「呢個網頁／App 內嵌咗《八字提要》全文」——書喺你嘅知識／用戶另行提供，你只係被要求跟提要框架論命。
3. 輸出必須用繁體中文（可用粵語口語解說，但術語保持子平傳統）。
4. 結構化分段，依次寫清（每段標題保留）：
   （一）格局
   （二）用神喜忌
   （三）性格
   （四）事業
   （五）財運
   （六）婚姻
   （七）大運流年（可概括近期／重點運，唔好流水帳亂猜）
5. 若用戶自述性格／際遇同原局時柱明顯唔合：主動提出「前後時辰校正」——用下面已填好嘅鄰近時柱，請用戶揀更貼嘅一柱再重論，唔好空講「可能差一時辰」而無干支。
   · 前一時辰時柱：${prevHourGz || '（無）'}
   · 後一時辰時柱：${nextHourGz || '（無）'}
6. 不確定處寫「待按《八字提要》核對」，唔好裝作有原書逐字引用。
7. 唔提供醫療、法律、投資保證；命理僅供參考。`;
}

/** 用戶盤數據區塊 */
export function buildChartPayload(input: LunmingPromptInput): string {
  const { gregorianLabel, gender, cmp } = input;
  const orig = cmp.charts.original;
  const prev = cmp.charts.prev;
  const next = cmp.charts.next;
  const prevLabel = formatSampleLabel(cmp.prev.sample);
  const nextLabel = formatSampleLabel(cmp.next.sample);

  const dayunHead = `方向：${orig.dayun.ruleLabel}（${orig.dayun.forward ? '順行' : '逆行'}）；起運約 ${orig.dayun.qiYunYears} 年 ${orig.dayun.qiYunMonths} 個月（節「${orig.dayun.jieName}」）`;

  return `【用戶命盤數據｜請據此論命】
公曆日時：${gregorianLabel}
性別：${gender}
日主：${orig.dayMaster}
節氣：${orig.calendar.jieqi.name}${orig.calendar.lunarDay != null ? `；農曆日：${orig.calendar.lunarDay}` : ''}

四柱：${formatPillarsLine(orig.pillars)}

十神摘要：
${formatShiShenSummary(orig)}

大運列表（${dayunHead}）：
${formatDayunList(orig.dayun.items)}

五行計數：
${formatWuxing(orig)}

前後時辰四柱（校正用）：
· 前一時辰（代表 ${prevLabel}，${cmp.prev.rangeLabel}）
  四柱：${formatPillarsLine(prev.pillars)}
  時柱：${hourGz(prev)}
· 後一時辰（代表 ${nextLabel}，${cmp.next.rangeLabel}）
  四柱：${formatPillarsLine(next.pillars)}
  時柱：${hourGz(next)}

請依上方【系統指示】完成論命。`;
}

/** 完整剪貼簿字串：系統指示 + 盤數據 */
export function buildLunmingPrompt(input: LunmingPromptInput): string {
  const prevGz = hourGz(input.cmp.charts.prev);
  const nextGz = hourGz(input.cmp.charts.next);
  const sys = buildSystemInstructions(prevGz, nextGz);
  const payload = buildChartPayload(input);
  return `${sys}\n\n───\n\n${payload}`;
}

/** 格式化公曆標籤（與 UI 一致） */
export function formatGregorianLabel(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
): string {
  return `${year}-${pad2(month)}-${pad2(day)} ${pad2(hour)}:${pad2(minute)}`;
}

/**
 * 未來「論命」API 預留（stub）
 * 而家唔呼叫任何遠端；唔需要 API key。
 * 之後可改為 POST chart + prompt 到自建 endpoint。
 */
export interface LunmingApiStub {
  enabled: false;
  note: string;
}

export const LUNMING_API_STUB: LunmingApiStub = {
  enabled: false,
  note: '論命 API 未接線：而家只提供「複製論命提示詞」畀外部 AI。接線後可喺此呼叫自建服務，無需喺瀏覽器存書商 API key。',
};
